<script setup lang="ts">
import { SDivider, SElevationTokens, SSheet } from 'sima-suite-components-vue3'
import { useTranslator } from 'sima-suite-translator-vue3'
import { computed, defineAsyncComponent, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useLocale } from 'vuetify'

import OcsBrandLogo from '@/components/atoms/OcsBrandLogo/OcsBrandLogo.vue'
import ButtonPanel from '@/components/organims/ButtonPanel/ButtonPanel.vue'
import SWizard from '@/components/organims/SWizard/SWizard.vue'
import { useHost, useStepper } from '@/composables'
import { AppointmentRequest } from '@/core/appointment/domain/Appointment.ts'
import { useDependencies } from '@/core/shared/infra/composables/useDependencies.ts'
import {
  ROUTE_ALREADY_HAS_APPOINTMENT,
  ROUTE_APPOINTMENT_CONFIRM,
  ROUTE_APPOINTMENT_FAILURE,
  ROUTE_REDIRECT_BRAND
} from '@/router/routeNames.ts'
import { useAppointmentStore } from '@/store'
import { changeLocationHash } from '@/utils'
import GoogleTagManagerWrapper from '@/utils/googleTagManagerWrapper.js'
import HotjarWrapper from '@/utils/hotjarWrapper.js'

const { brandColor, brandLogo, brandsWeb } = useHost()

const appointmentStore = useAppointmentStore()

const isLoading = ref(false)

const logo = computed(() => {
  if (brandLogo.value) {
    return new URL(`../../assets/themes/logos/${brandLogo.value}`, import.meta.url).href
  }
  return ''
})

const { language, locale, translate } = useTranslator()
const { current: currentLocale } = useLocale()

const googleTagManager = new GoogleTagManagerWrapper()
const hotjar = new HotjarWrapper()

currentLocale.value = language.value

const wizardTabs = ref<string[]>([
  translate('AppointmentWizard.Steps.Step1'),
  translate('AppointmentWizard.Steps.Step2'),
  translate('AppointmentWizard.Steps.Step3'),
  translate('AppointmentWizard.Steps.Step4'),
  translate('AppointmentWizard.Steps.Step5'),
  translate('AppointmentWizard.Steps.Step6'),
  translate('AppointmentWizard.Steps.Step7')
])

const { component, isFirstStep, isLastStep, next, previous, setStep, step } = useStepper({
  steps: [
    defineAsyncComponent(() => import('@/containers/WorkshopStep/WorkshopStep.vue')),
    defineAsyncComponent(() => import('@/containers/VehicleStep/VehicleStep.vue')),
    defineAsyncComponent(() => import('@/containers/ServicesStep/ServicesStep.vue')),
    defineAsyncComponent(() => import('@/containers/AppointmentStep/AppointmentStep.vue')),
    defineAsyncComponent(
      () => import('@/containers/AdviserAvailabilityStep/AdviserAvailabilityStep.vue')
    ),
    defineAsyncComponent(() => import('@/containers/ContactDetailsStep/ContactDetailsStep.vue')),
    defineAsyncComponent(
      () => import('@/containers/ConfirmAppointmentStep/ConfirmAppointmentStep.vue')
    )
  ]
})

const stepsName = [
  'Workshops',
  'VehicleForm',
  'WorkshopServices',
  'AppointmentDate',
  'AdviserAvailability',
  'ContactForm',
  'SummaryAppointment'
]

const showFeedback = ref(false)

const currentComponent = computed(() => {
  if (showFeedback.value && step.value === 1) {
    return defineAsyncComponent(
      () => import('@/containers/GetVehicleInformationFeedback/GetVehicleInformationFeedback.vue')
    )
  }

  if (showFeedback.value && step.value === 2) {
    return defineAsyncComponent(
      () => import('@/containers/GetPPSOPackageStep/GetPPSOPackageStep.vue')
    )
  }

  return component.value
})

onMounted(() => {
  appointmentStore.reset()
  changeLocationHash(stepsName[step.value])
  hotjar.stateChange(window.location.href)
})

const { getPPSOPackagesUseCase, getVehicleByPlateUseCase } = useDependencies()

const getVehicleByPlate = async () => {
  const vehicle = await getVehicleByPlateUseCase.execute(appointmentStore.vehicle.plate)
  if (vehicle) {
    appointmentStore.vehicle.brandCode = vehicle.brandCode
    appointmentStore.vehicle.vin = vehicle.vin
    appointmentStore.vehicle.brand = vehicle.brand
    appointmentStore.vehicle.model = vehicle.model
    appointmentStore.vehicle.plate = vehicle.plate
  }
}

const getPPSOPackages = async () => {
  appointmentStore.ppsoPackages = await getPPSOPackagesUseCase.execute({
    brandCode: appointmentStore.vehicle.brandCode,
    culture: locale.value,
    hostId: hostId.value,
    mileage: appointmentStore.vehicle.kilometers,
    vin: appointmentStore.vehicle.vin,
    workshopId: appointmentStore.workshop.id
  })
}

const nextStep = async () => {
  if (appointmentStore.workshop.redirectUrl) {
    googleTagManager.trackStep2Exit()
    window.location.href = appointmentStore.workshop.redirectUrl
  }
  if (import.meta.env.MODE === 'production') {
    googleTagManager.registerPageViewEvent(step, appointmentStore)
  }
  if (step.value === 1) {
    showFeedback.value = true
    try {
      isLoading.value = true
      await getVehicleByPlate()
      const isAllowBrand = appointmentStore.workshop.brands?.some(
        (brand) => brand.code === appointmentStore.vehicle.brandCode
      )
      const brandsMapping = Object.keys(brandsWeb.value)
      const hasBrandMapping = brandsMapping.includes(appointmentStore.vehicle.brandCode)
      if (appointmentStore.vehicle.vin && !isAllowBrand && hasBrandMapping) {
        router.push({
          name: ROUTE_REDIRECT_BRAND
        })
        return
      }
    } catch {
      next()
      changeLocationHash(stepsName[step.value])
      hotjar.stateChange(window.location.href)
      return
    } finally {
      showFeedback.value = false
      isLoading.value = false
    }
  }
  if (step.value === 2 && appointmentStore.vehicle.vin) {
    showFeedback.value = true
    try {
      isLoading.value = true
      await getPPSOPackages()
      if (appointmentStore.ppsoPackages.length) {
        const servicesTotalUdt = appointmentStore.services.reduce(
          (total, service) => total + (!service.nextServicesAvailable ? service.udt : 0),
          0
        )
        const packagesTotalUdt = appointmentStore.ppsoPackages.reduce(
          (total, pack) => total + pack.udt,
          0
        )
        appointmentStore.appointment.servicesTotalUdts = packagesTotalUdt + servicesTotalUdt
      }
    } catch {
      next()
      changeLocationHash(stepsName[step.value])
      hotjar.stateChange(window.location.href)
      return
    } finally {
      showFeedback.value = false
      isLoading.value = false
    }
  }

  next()
  changeLocationHash(stepsName[step.value])
  hotjar.stateChange(window.location.href)
}

const previousStep = () => {
  previous()
  changeLocationHash(stepsName[step.value])
  hotjar.stateChange(window.location.href)
}

const router = useRouter()

const isServiceValid = computed(() => {
  const services = appointmentStore.services
  const someWithoutRemark = services.some((service) => service.isSelected && !service.hasRemarks)
  const someWithRemark = services.some(
    (service) =>
      service.isSelected &&
      service.hasRemarks &&
      service.remarks!.length > 4 &&
      service.remarks!.length <= 70
  )

  const everyWithoutRemark = services.every((service) => service.isSelected && !service.hasRemarks)
  const everyWithRemark = services.every(
    (service) =>
      service.isSelected &&
      service.hasRemarks &&
      service.remarks!.length > 4 &&
      service.remarks!.length <= 70
  )

  if (!services.length) {
    return false
  }

  if (someWithRemark && someWithoutRemark) {
    return true
  }

  if (everyWithRemark) {
    return true
  }
  return everyWithoutRemark
})

const handleWizardUpdateStep = (value: number) => {
  setStep(value)
  changeLocationHash(stepsName[step.value])
  hotjar.stateChange(window.location.href)
}

const isNextDisabled = computed(() => {
  if (showFeedback.value) {
    return true
  }
  switch (step.value) {
    case 0:
      return isNaN(appointmentStore.workshop.id!)
    case 1:
      return !appointmentStore.isVehicleFormValid
    case 2:
      return !isServiceValid.value
    case 3: {
      const hasDateSelected = appointmentStore.date
      const hasHourSelected = Number.isNaN(appointmentStore.availabilityRange)
      return !(hasDateSelected && !hasHourSelected)
    }
    case 4:
      return !appointmentStore.adviser
    case 5:
      return !appointmentStore.isCustomerFormValid
  }

  return false
})

const { createAppointmentUseCase } = useDependencies()
const { id: hostId } = useHost()

const confirmAppointment = async () => {
  isLoading.value = true
  const appointmentRequest: AppointmentRequest = {
    adviser: appointmentStore.adviser!,
    availability: appointmentStore.availability,
    campaigns: appointmentStore.campaigns,
    customer: appointmentStore.customer,
    date: appointmentStore.date,
    hostId: hostId.value,
    packages: appointmentStore.isPPSOPackageNeeded ? appointmentStore.ppsoPackages : [],
    services: appointmentStore.services,
    vehicle: appointmentStore.vehicle,
    workshop: appointmentStore.workshop
  }
  try {
    appointmentStore.wasSentRequest = true
    await createAppointmentUseCase.execute(appointmentRequest)
    googleTagManager.trackStep8()
    router.push({
      name: ROUTE_APPOINTMENT_CONFIRM,
      state: {
        description: translate('Success.CreateAppointment.Description'),
        title: translate('Success.CreateAppointment.Title')
      }
    })
  } catch (error: any) {
    const { Status: statusCode } = error
    if (statusCode === 409) {
      router.push({
        name: ROUTE_ALREADY_HAS_APPOINTMENT,
        state: {
          appointmentId: error.Extensions.OrderId,
          enterpriseId: appointmentStore.workshop.simaId.toString()
        }
      })
    } else {
      router.push({
        name: ROUTE_APPOINTMENT_FAILURE,
        state: {
          description: translate('Error.CreateAppointment.Description'),
          title: translate('Error.CreateAppointment.Title')
        }
      })
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <s-sheet
    :elevation="SElevationTokens.null"
    class="w-full bg-color-white md:ml-auto md:mr-auto md:mt-spacing-xxxl md:h-fit md:w-3/4 md:shadow-shadow-m lg:w-3/5 lg:max-w-[1200px]"
  >
    <div class="flex flex-col items-center p-spacing-s sm:justify-center md:justify-evenly">
      <ocs-brand-logo :src="logo" :alt="translate('AppointmentWizard.LogoAlt')" />
      <div class="w-full">
        <s-wizard
          :color="brandColor"
          :step="step"
          :steps="wizardTabs"
          @update:step="handleWizardUpdateStep"
        />
      </div>
    </div>
    <div class="pt-1">
      <s-divider />
    </div>
    <div
      class="h-[calc(100dvh_-_17rem)] px-spacing-m pt-spacing-s sm:h-[calc(100dvh_-_19.25rem)] md:h-[calc(100dvh_-_28rem)]"
    >
      <keep-alive>
        <component :is="currentComponent" />
      </keep-alive>
    </div>
    <div
      class="vw-integrator-actions fixed bottom-0 h-fit w-full rounded-b-2xl bg-color-white shadow-[0_-4px_4px_0_rgba(173,173,173,0.1)] sm:relative sm:shadow-none"
    >
      <button-panel
        :is-next-disabled="isNextDisabled"
        :is-first-step="isFirstStep"
        :is-last-step="isLastStep"
        :is-confirm-loading="isLoading"
        @click:next="nextStep"
        @click:back="previousStep"
        @click:confirm="confirmAppointment"
      />
    </div>
  </s-sheet>
</template>
